import { FormEvent, useEffect, useState } from "react"
import { apiFetch, getAuthHeaders } from "../../lib/api"
import { redirectToLoginOnUnauthorized } from "../../lib/auth"
import { useEmpresa } from "../../app/context/EmpresaContext"
import { config } from "../../lib/config"

const PAGE_SIZE = 50

type Situacao =
  | "emitida"
  | "cancelada"
  | "denegada"
  | "rejeitada"
  | "em_andamento"
  | "inutilizada"
  | "inutilizacao_pendente"
  | "vazia"
  | string

type FaixaItem = {
  numero: number
  situacao: Situacao
  ano?: number | null
  documento_id?: string | null
  status?: string | null
  chave_acesso?: string | null
  protocolo?: string | null
  criado_em?: string | null
  inutilizacao_id?: string | null
  inutilizacao_protocolo?: string | null
  justificativa?: string | null
}

type FaixaLista = {
  ultimo_numero: number
  numero_inicial: number
  numero_final: number
  total: number
  page: number
  page_size: number
  com_arquivo: number
  resumo: Record<string, number>
  items: FaixaItem[]
}

type FiltroFaixa = {
  serie: number
  ano: number
  ambiente: 1 | 2
  de: string
  ate: string
  numeroInicial: string
  numeroFinal: string
}

const situacaoLabel = (situacao: Situacao) => {
  if (situacao === "emitida") return "Emitida"
  if (situacao === "cancelada") return "Cancelada"
  if (situacao === "denegada") return "Denegada"
  if (situacao === "rejeitada") return "Rejeitada"
  if (situacao === "falha_comunicacao") return "Falha de comunicação"
  if (situacao === "em_andamento") return "Em andamento"
  if (situacao === "inutilizada") return "Inutilizada"
  if (situacao === "inutilizacao_pendente") return "Inutilização na fila"
  if (situacao === "vazia") return "Vazia"
  return situacao
}

const situacaoClass = (situacao: Situacao) => {
  if (situacao === "emitida") return "bg-emerald-100 text-emerald-800"
  if (situacao === "cancelada") return "bg-slate-200 text-slate-800"
  if (situacao === "denegada") return "bg-orange-100 text-orange-900"
  if (situacao === "rejeitada" || situacao === "falha_comunicacao") return "bg-red-100 text-red-800"
  if (situacao === "inutilizada") return "bg-violet-100 text-violet-900"
  if (situacao === "em_andamento" || situacao === "inutilizacao_pendente") return "bg-amber-100 text-amber-900"
  return "bg-slate-100 text-slate-600"
}

const formatWhen = (iso?: string | null) => {
  if (!iso) return "—"
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return "—"
  return date.toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo", dateStyle: "short", timeStyle: "short" })
}

const saveBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

const parseApiError = (err: unknown, fallback: string) => {
  if (!(err instanceof Error)) return fallback
  try {
    const parsed = JSON.parse(err.message) as { detail?: string }
    if (typeof parsed.detail === "string") return parsed.detail
  } catch {
    /* texto bruto */
  }
  return err.message || fallback
}

const paramsDaFaixa = (empresaId: string, filtro: FiltroFaixa, extra?: Record<string, string>) => {
  const params = new URLSearchParams({
    serie: String(filtro.serie),
    ano: String(filtro.ano),
    ambiente: String(filtro.ambiente),
  })
  if (filtro.de) params.set("de", filtro.de)
  if (filtro.ate) params.set("ate", filtro.ate)
  if (filtro.numeroInicial) params.set("numero_inicial", filtro.numeroInicial)
  if (filtro.numeroFinal) params.set("numero_final", filtro.numeroFinal)
  Object.entries(extra ?? {}).forEach(([key, value]) => params.set(key, value))
  return `/v1/empresas/${empresaId}/nfe/serie/faixa?${params}`
}

export const NfeFaixaPage = () => {
  const { empresaId } = useEmpresa()
  const [serie, setSerie] = useState("1")
  const [ano, setAno] = useState(String(new Date().getFullYear()))
  const [ambiente, setAmbiente] = useState<1 | 2>(1)
  const [de, setDe] = useState("")
  const [ate, setAte] = useState("")
  const [numeroInicial, setNumeroInicial] = useState("")
  const [numeroFinal, setNumeroFinal] = useState("")
  const [filtro, setFiltro] = useState<FiltroFaixa | null>(null)
  const [page, setPage] = useState(1)
  const [lista, setLista] = useState<FaixaLista | null>(null)
  const [loading, setLoading] = useState(false)
  const [baixando, setBaixando] = useState<"xml" | "danfe" | "ambos" | null>(null)
  const [message, setMessage] = useState("")
  const [isError, setIsError] = useState(false)

  const loadFaixa = async (alvo: FiltroFaixa, pg: number) => {
    if (!empresaId) return
    setLoading(true)
    try {
      const data = await apiFetch<FaixaLista>(
        paramsDaFaixa(empresaId, alvo, { page: String(pg), page_size: String(PAGE_SIZE) }),
      )
      setLista(data)
      setIsError(false)
      setMessage("")
    } catch (err) {
      setLista(null)
      setIsError(true)
      setMessage(parseApiError(err, "Não foi possível listar a faixa"))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!filtro || !empresaId) return
    loadFaixa(filtro, page)
  }, [filtro, page, empresaId])

  const handleConsultar = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const serieNum = Number(serie)
    const anoNum = Number(ano)
    if (!Number.isInteger(serieNum) || serieNum < 1) {
      setIsError(true)
      setMessage("Informe a série.")
      return
    }
    if (!Number.isInteger(anoNum) || anoNum < 2000) {
      setIsError(true)
      setMessage("Informe o ano da numeração.")
      return
    }
    if (numeroInicial && numeroFinal && Number(numeroFinal) < Number(numeroInicial)) {
      setIsError(true)
      setMessage("O número final não pode ser menor que o inicial.")
      return
    }
    setPage(1)
    setFiltro({
      serie: serieNum,
      ano: anoNum,
      ambiente,
      de,
      ate,
      numeroInicial,
      numeroFinal,
    })
  }

  const downloadZip = async (incluir: "xml" | "danfe" | "ambos") => {
    if (!empresaId || !filtro) return
    setBaixando(incluir)
    try {
      const path = paramsDaFaixa(empresaId, filtro, { incluir }).replace("/nfe/serie/faixa?", "/nfe/serie/faixa/arquivos.zip?")
      const res = await fetch(`${config.apiUrl}${path}`, { headers: getAuthHeaders() })
      if (res.status === 401) {
        redirectToLoginOnUnauthorized()
        return
      }
      if (!res.ok) {
        setIsError(true)
        const text = await res.text()
        setMessage(parseApiError(new Error(text), "Não foi possível gerar o ZIP"))
        return
      }
      const amb = filtro.ambiente === 1 ? "prod" : "hom"
      saveBlob(await res.blob(), `nfe-serie-${filtro.serie}-${filtro.ano}-${amb}.zip`)
      setIsError(false)
      setMessage("ZIP gerado com sucesso.")
    } finally {
      setBaixando(null)
    }
  }

  const downloadUm = async (item: FaixaItem, tipo: "xml" | "danfe") => {
    if (!empresaId || !item.documento_id) return
    const res = await fetch(`${config.apiUrl}/v1/empresas/${empresaId}/nfe/${item.documento_id}/${tipo === "xml" ? "xml" : "danfe"}`, {
      headers: getAuthHeaders(),
    })
    if (res.status === 401) {
      redirectToLoginOnUnauthorized()
      return
    }
    if (!res.ok) {
      setIsError(true)
      const text = await res.text()
      setMessage(parseApiError(new Error(text), tipo === "xml" ? "Não foi possível baixar o XML." : "Não foi possível baixar o DANFE."))
      return
    }
    const nome = (item.chave_acesso || `${filtro?.serie}-${String(item.numero).padStart(9, "0")}`).replace(/\D/g, "") || String(item.numero)
    saveBlob(await res.blob(), `${nome}.${tipo === "xml" ? "xml" : "pdf"}`)
  }

  const totalPages = lista ? Math.max(1, Math.ceil(lista.total / PAGE_SIZE)) : 1
  const temArquivo = (lista?.com_arquivo ?? 0) > 0
  const zipBloqueado = !temArquivo || (lista?.com_arquivo ?? 0) > 100 || baixando !== null

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Faixa da série</h1>
        <p className="mt-1 text-sm text-slate-600">
          Numeração da empresa ativa, do nº 1 até o último número que esta série já alcançou no ano e no ambiente.
          Cada número aparece como nota emitida, cancelada, vazia ou inutilizada.
          Inutilização só entra depois que a SEFAZ registra. Pedido na fila, rejeitado ou ainda não processado não ocupa o número.
        </p>
      </div>

      {!empresaId && (
        <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Selecione uma empresa em <strong>Empresa ativa</strong> na barra lateral.
        </p>
      )}

      {message ? (
        <div
          role="status"
          className={`rounded-xl border px-4 py-3 text-sm ${
            isError ? "border-red-200 bg-red-50 text-red-900" : "border-emerald-200 bg-emerald-50 text-emerald-950"
          }`}
        >
          {message}
        </div>
      ) : null}

      <form onSubmit={handleConsultar} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="text-sm text-slate-700">
            Série
            <input
              type="number"
              min={1}
              required
              value={serie}
              onChange={(event) => setSerie(event.target.value)}
              className="mt-1 block w-full rounded-lg border border-slate-300 px-2 py-1.5"
              aria-label="Série da NF-e"
            />
          </label>
          <label className="text-sm text-slate-700">
            Ano
            <input
              type="number"
              min={2000}
              max={2100}
              required
              value={ano}
              onChange={(event) => setAno(event.target.value)}
              className="mt-1 block w-full rounded-lg border border-slate-300 px-2 py-1.5"
              aria-label="Ano da numeração"
            />
          </label>
          <label className="text-sm text-slate-700">
            Nº inicial
            <input
              type="number"
              min={1}
              value={numeroInicial}
              onChange={(event) => setNumeroInicial(event.target.value)}
              className="mt-1 block w-full rounded-lg border border-slate-300 px-2 py-1.5"
              aria-label="Número inicial da faixa"
            />
          </label>
          <label className="text-sm text-slate-700">
            Nº final
            <input
              type="number"
              min={1}
              value={numeroFinal}
              onChange={(event) => setNumeroFinal(event.target.value)}
              className="mt-1 block w-full rounded-lg border border-slate-300 px-2 py-1.5"
              aria-label="Número final da faixa"
            />
          </label>
          <label className="text-sm text-slate-700">
            De
            <input
              type="date"
              value={de}
              onChange={(event) => setDe(event.target.value)}
              className="mt-1 block w-full rounded-lg border border-slate-300 px-2 py-1.5"
              aria-label="Data inicial"
            />
          </label>
          <label className="text-sm text-slate-700">
            Até
            <input
              type="date"
              value={ate}
              onChange={(event) => setAte(event.target.value)}
              className="mt-1 block w-full rounded-lg border border-slate-300 px-2 py-1.5"
              aria-label="Data final"
            />
          </label>
          <div className="text-sm text-slate-700">
            Ambiente
            <div className="mt-1 flex gap-1 rounded-lg border border-slate-200 p-0.5" role="group" aria-label="Ambiente">
              <button
                type="button"
                aria-pressed={ambiente === 1}
                onClick={() => setAmbiente(1)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium ${ambiente === 1 ? "bg-slate-800 text-white" : "text-slate-600"}`}
              >
                Produção
              </button>
              <button
                type="button"
                aria-pressed={ambiente === 2}
                onClick={() => setAmbiente(2)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium ${ambiente === 2 ? "bg-amber-100 text-amber-950" : "text-slate-600"}`}
              >
                Homologação
              </button>
            </div>
          </div>
        </div>
        <button
          type="submit"
          disabled={!empresaId || loading}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {loading ? "Consultando…" : "Consultar faixa"}
        </button>
        <p className="text-xs text-slate-500">
          Sem data, a lista inclui os números vazios. Com data, ficam só as notas e inutilizações daquele período.
          O ZIP baixa as emitidas e canceladas do filtro inteiro, não só da página atual (máximo de 100).
        </p>
      </form>

      {lista && filtro ? (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-3">
            <div>
              <h2 className="font-semibold text-slate-900">
                Série {filtro.serie} · {filtro.ano} · {filtro.ambiente === 1 ? "Produção" : "Homologação"}
              </h2>
              <p className="mt-0.5 text-xs text-slate-600">
                Último número no sistema: <strong>{lista.ultimo_numero || "—"}</strong>
                {" · "}
                {lista.total} número(s) nesta consulta
                {" · "}
                {lista.com_arquivo} com XML/DANFE
              </p>
              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                {(
                  [
                    ["emitida", "Emitidas"],
                    ["cancelada", "Canceladas"],
                    ["inutilizada", "Inutilizadas"],
                    ["vazia", "Vazias"],
                    ["rejeitada", "Rejeitadas"],
                    ["falha_comunicacao", "Falha de comunicação"],
                    ["em_andamento", "Em andamento"],
                    ["inutilizacao_pendente", "Inutilização na fila"],
                    ["denegada", "Denegadas"],
                  ] as const
                )
                  .filter(([key]) => (lista.resumo[key] ?? 0) > 0)
                  .map(([key, label]) => (
                    <span key={key} className={`rounded px-2 py-0.5 font-medium ${situacaoClass(key)}`}>
                      {label}: {lista.resumo[key]}
                    </span>
                  ))}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={zipBloqueado}
                onClick={() => downloadZip("ambos")}
                className="rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {baixando === "ambos" ? "Gerando…" : "XML + DANFE"}
              </button>
              <button
                type="button"
                disabled={zipBloqueado}
                onClick={() => downloadZip("xml")}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                {baixando === "xml" ? "Gerando…" : "Só XML"}
              </button>
              <button
                type="button"
                disabled={zipBloqueado}
                onClick={() => downloadZip("danfe")}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                {baixando === "danfe" ? "Gerando…" : "Só DANFE"}
              </button>
            </div>
          </div>
          {(lista.com_arquivo ?? 0) > 100 ? (
            <p className="border-b border-amber-200 bg-amber-50 px-5 py-2 text-sm text-amber-950">
              Há {lista.com_arquivo} notas com arquivo. Reduza a data ou a faixa para baixar no máximo 100 por ZIP.
            </p>
          ) : null}
          {loading ? (
            <p className="p-5 text-sm text-slate-600">Carregando…</p>
          ) : lista.items.length === 0 ? (
            <p className="p-5 text-sm text-slate-600">Nenhum número nesta série, ano e ambiente.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2 font-medium">Número</th>
                    <th className="px-4 py-2 font-medium">Situação</th>
                    <th className="px-4 py-2 font-medium">Data</th>
                    <th className="px-4 py-2 font-medium">Protocolo</th>
                    <th className="px-4 py-2 font-medium">Chave / justificativa</th>
                    <th className="px-4 py-2 font-medium">Arquivos</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {lista.items.map((item) => {
                    const baixavel = item.situacao === "emitida" || item.situacao === "cancelada"
                    const podeXml = baixavel || item.situacao === "rejeitada" || item.situacao === "falha_comunicacao"
                    return (
                      <tr key={`${item.numero}-${item.documento_id ?? item.inutilizacao_id ?? "vazio"}`}>
                        <td className="px-4 py-3 font-mono font-medium text-slate-900">
                          {String(item.numero).padStart(9, "0")}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`rounded px-2 py-0.5 text-xs font-medium ${situacaoClass(item.situacao)}`}>
                            {situacaoLabel(item.situacao)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-600">{formatWhen(item.criado_em)}</td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-700">{item.protocolo || "—"}</td>
                        <td className="max-w-xs px-4 py-3 text-xs text-slate-600">
                          {item.chave_acesso || item.justificativa || "—"}
                        </td>
                        <td className="px-4 py-3">
                          {(podeXml || baixavel) && item.documento_id ? (
                            <div className="flex gap-2">
                              {podeXml ? (
                                <button
                                  type="button"
                                  onClick={() => downloadUm(item, "xml")}
                                  className="text-sm font-medium text-brand-700 hover:underline"
                                >
                                  XML
                                </button>
                              ) : null}
                              {baixavel ? (
                                <button
                                  type="button"
                                  onClick={() => downloadUm(item, "danfe")}
                                  className="text-sm font-medium text-brand-700 hover:underline"
                                >
                                  DANFE
                                </button>
                              ) : null}
                            </div>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
          {lista.total > PAGE_SIZE ? (
            <div className="flex items-center justify-between border-t border-slate-200 px-5 py-3 text-sm">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                className="rounded-lg border border-slate-300 px-3 py-1.5 disabled:opacity-50"
              >
                Anterior
              </button>
              <span className="text-slate-600">
                Página {page} de {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((current) => current + 1)}
                className="rounded-lg border border-slate-300 px-3 py-1.5 disabled:opacity-50"
              >
                Próxima
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
